import { useState } from 'react';

import { IconButton, Icons, Tooltip } from '@commenda/kakashi';
import { ChatRoomAgentFeedbackRating } from '@commenda/types';
import { cn } from '@commenda/utils';

import type { AgentReplyActions } from '../../ChatRoom/types';

interface AgentReplyControlsProps {
  messageId: string;
  myRating: ChatRoomAgentFeedbackRating | null;
  canRate: boolean;
  agentReplyActions: AgentReplyActions;
}

const SELECTED_AGENT_REPLY_CONTROL_CLASS_NAME = 'bg-bg-tertiary text-fg-brand';

const AgentReplyControls = ({
  messageId,
  myRating,
  canRate,
  agentReplyActions,
}: AgentReplyControlsProps) => {
  const { isAgentMuted, onAgentReplyRate, onAgentMuteChange } =
    agentReplyActions;
  const [localRating, setLocalRating] =
    useState<ChatRoomAgentFeedbackRating | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const displayedRating = localRating ?? myRating;

  const onRatingSelect = async (rating: ChatRoomAgentFeedbackRating) => {
    if (isSaving || rating === displayedRating) return;
    const previousRating = localRating;
    setLocalRating(rating);
    setIsSaving(true);
    const isSaved = await onAgentReplyRate(messageId, rating);
    if (!isSaved) setLocalRating(previousRating);
    setIsSaving(false);
  };

  const onMuteToggle = async () => {
    if (isSaving) return;
    setIsSaving(true);
    await onAgentMuteChange(!isAgentMuted);
    setIsSaving(false);
  };

  const isUpSelected = displayedRating === ChatRoomAgentFeedbackRating.UP;
  const isDownSelected = displayedRating === ChatRoomAgentFeedbackRating.DOWN;

  return (
    <div className="flex items-center gap-xs" aria-busy={isSaving}>
      {canRate && (
        <>
          <Tooltip content="Good response">
            <IconButton
              size="sm"
              aria-label="Good response"
              aria-pressed={isUpSelected}
              disabled={isSaving}
              className={cn(
                isUpSelected && SELECTED_AGENT_REPLY_CONTROL_CLASS_NAME
              )}
              onClick={() => onRatingSelect(ChatRoomAgentFeedbackRating.UP)}
              icon={
                <Icons.ThumbsUp
                  className="size-4"
                  aria-hidden="true"
                  fill={isUpSelected ? 'currentColor' : 'none'}
                />
              }
            />
          </Tooltip>
          <Tooltip content="Bad response">
            <IconButton
              size="sm"
              aria-label="Bad response"
              aria-pressed={isDownSelected}
              disabled={isSaving}
              className={cn(
                isDownSelected && SELECTED_AGENT_REPLY_CONTROL_CLASS_NAME
              )}
              onClick={() => onRatingSelect(ChatRoomAgentFeedbackRating.DOWN)}
              icon={
                <Icons.ThumbsDown
                  className="size-4"
                  aria-hidden="true"
                  fill={isDownSelected ? 'currentColor' : 'none'}
                />
              }
            />
          </Tooltip>
        </>
      )}
      <Tooltip
        content={
          isAgentMuted
            ? 'Resume Commenda Agent in this thread'
            : 'Mute Commenda Agent in this thread'
        }
      >
        <IconButton
          size="sm"
          aria-label={
            isAgentMuted ? 'Resume Commenda Agent' : 'Mute Commenda Agent'
          }
          aria-pressed={isAgentMuted}
          disabled={isSaving}
          className={cn(
            isAgentMuted && SELECTED_AGENT_REPLY_CONTROL_CLASS_NAME
          )}
          onClick={onMuteToggle}
          icon={<Icons.VolumeX className="size-4" aria-hidden="true" />}
        />
      </Tooltip>
    </div>
  );
};

export default AgentReplyControls;
